import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/frontend/lib/utils";

const segmentedControlVariants = cva("flex rounded-md", {
  variants: {
    size: {
      sm: "gap-0.5 bg-muted/30 p-0.5",
      md: "gap-0.5 bg-muted/30 p-0.5",
      lg: "gap-1 bg-muted/40 p-1",
    },
    disabled: {
      true: "pointer-events-none opacity-50",
      false: "",
    },
  },
  defaultVariants: {
    size: "sm",
    disabled: false,
  },
});

const segmentedControlItemVariants = cva("rounded transition-colors", {
  variants: {
    size: {
      sm: "px-3 py-1 text-[10px]",
      md: "px-4 py-1.5 text-xs font-medium",
      lg: "flex-1 px-4 py-2 text-xs font-semibold tracking-wide",
    },
    variant: {
      subtle: "",
      emphasis: "",
    },
    active: {
      true: "",
      false: "",
    },
  },
  compoundVariants: [
    { variant: "subtle", active: true, class: "bg-muted text-blue-400" },
    {
      variant: "subtle",
      active: false,
      class: "text-muted-foreground hover:bg-muted/60 hover:text-foreground",
    },
    { variant: "emphasis", active: true, class: "bg-foreground text-background" },
    {
      variant: "emphasis",
      active: false,
      class: "text-muted-foreground hover:bg-muted/60 hover:text-foreground",
    },
  ],
  defaultVariants: {
    size: "sm",
    variant: "subtle",
    active: false,
  },
});

type SegmentedControlOption<T extends string> = {
  value: T;
  label: string;
};

type SegmentedControlProps<T extends string> = Omit<
  React.ComponentProps<"div">,
  "onChange"
> &
  Pick<VariantProps<typeof segmentedControlVariants>, "size"> &
  Pick<VariantProps<typeof segmentedControlItemVariants>, "variant"> & {
    value: T;
    onChange: (value: T) => void;
    options: ReadonlyArray<SegmentedControlOption<T>>;
    disabled?: boolean;
  };

function SegmentedControl<T extends string>({
  value,
  onChange,
  options,
  disabled = false,
  size = "sm",
  variant = "subtle",
  className,
  ...rest
}: SegmentedControlProps<T>) {
  return (
    <div
      data-slot="segmented-control"
      data-size={size}
      data-variant={variant}
      className={cn(segmentedControlVariants({ size, disabled }), className)}
      {...rest}
    >
      {options.map((opt) => {
        const active = value === opt.value;
        return (
          <button
            key={opt.value}
            type="button"
            data-active={active}
            onClick={() => onChange(opt.value)}
            className={cn(
              segmentedControlItemVariants({ size, variant, active }),
            )}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}

export {
  SegmentedControl,
  segmentedControlVariants,
  segmentedControlItemVariants,
};
export type { SegmentedControlProps, SegmentedControlOption };
